import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Pagination from '../common/Pagination';
import PageHeader from '../common/PageHeader';
import PageState from '../common/PageState';
import QueueCreateModal from './QueueCreateModal';
import QueueEditModal from './QueueEditModal';
import QueueMemberEditModal from './QueueMemberEditModal';
import ConfirmModal from '../common/ConfirmModal';
import { useApi } from '../../hooks/useApi';
import { toast } from 'react-hot-toast';
import * as T from '../../styles/common/table.styled';
import { Button } from '../../styles/common/Button.styled';
import { isTableRowActionTarget } from '../../utils/tableInteraction';
import DebouncedSearchInput from '../common/DebouncedSearchInput';

const getStrategyLabel = (s: string) => {
  switch (s) {
    case 'PARALLEL': return '동시콜';
    case 'SEQUENTIAL': return '순차콜';
    default:            return s;
  }
};

const formatTimeout = (timeout?: number) => timeout == null ? '-' : `${timeout}초`;

const QueueList = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isMemberEditModalOpen, setIsMemberEditModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const { mode } = useAuth();
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const requestSequenceRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [searchTerm, setSearchTerm] = useState(appliedSearchTerm);
  const { request } = useApi();
  const [queues, setQueues] = useState<any[]>([]);

  const fetchQueues = useCallback(async () => {
    const requestMode = mode;
    const requestSequence = ++requestSequenceRef.current;
    setLoading(true);
    setHasLoadError(false);
    try {
      const data = await request({
        url: '/ringing-group',
        params: { limit: 20, pg: currentPage, displayName: appliedSearchTerm || undefined },
      });
      if (modeRef.current !== requestMode || requestSequenceRef.current !== requestSequence) return;
      if (data?.content) {
        setQueues(data.content);
        setTotalPages(data.totalPages ?? (data.content.length ? 1 : 0));
        setTotalElements(data.totalElements || 0);
      } else {
        setQueues(Array.isArray(data) ? data : []);
        setTotalPages(Array.isArray(data) && data.length ? 1 : 0);
        setTotalElements(Array.isArray(data) ? data.length : 0);
      }
    } catch {
      if (modeRef.current !== requestMode || requestSequenceRef.current !== requestSequence) return;
      setHasLoadError(true);
      toast.error('수신그룹 목록을 불러오는데 실패했습니다.');
    } finally {
      if (modeRef.current === requestMode && requestSequenceRef.current === requestSequence) setLoading(false);
    }
  }, [request, currentPage, appliedSearchTerm, mode]);

  useEffect(() => { fetchQueues(); }, [fetchQueues]);

  const handleSearch = (nextSearchTerm?: string) => {
    setAppliedSearchTerm(typeof nextSearchTerm === 'string' ? nextSearchTerm : searchTerm);
    setCurrentPage(1);
  };

  const handleConfirmDelete = async () => {
    if (!selectedQueue) return;
    try {
      await request({ url: `/ringing-group/${selectedQueue.id}`, method: 'DELETE' });
      toast.success('수신그룹이 삭제되었습니다.');
      setIsConfirmOpen(false);
      setSelectedQueue(null);
      fetchQueues();
    } catch {
      toast.error('수신그룹 삭제에 실패했습니다.');
    }
  };

  return (
    <T.PageContainer>
      <PageHeader title="수신그룹 관리" count={loading || hasLoadError ? undefined : totalElements} actions={<Button
          onClick={() => setIsCreateModalOpen(true)}
        >
          + 수신그룹 생성
        </Button>} />
      <T.TopBar>
        <T.SearchGroup>
          <T.SearchInput as={DebouncedSearchInput}
            aria-label="수신그룹명 검색"
            placeholder="수신그룹명 검색"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onSearch={handleSearch}
            delay={250}
          />
          <T.SearchButton onClick={() => handleSearch()}>검색</T.SearchButton>
        </T.SearchGroup>
      </T.TopBar>

      <T.TableArea $stickyActions $minWidth={960}>
        <T.Table>
          <colgroup>
            <col style={{ width: '60px' }} />
            <col style={{ width: '200px' }} />
            <col style={{ width: '120px' }} />
            <col style={{ width: '100px' }} />
            <col style={{ width: '120px' }} />
            <col style={{ width: '120px' }} />
            <col style={{ width: '220px' }} />
          </colgroup>
          <thead>
            <tr>
              <T.Th>No.</T.Th>
              <T.Th>수신그룹명</T.Th>
              <T.Th>콜분배방식</T.Th>
              <T.Th>내선 수</T.Th>
              <T.Th>개별 벨 울림</T.Th>
              <T.Th>전체 대기시간</T.Th>
              <T.Th>관리</T.Th>
            </tr>
          </thead>
          <tbody>
            {loading ? <T.Tr><T.Td colSpan={7}><PageState status="loading" /></T.Td></T.Tr>
            : hasLoadError ? <T.Tr><T.Td colSpan={7}><PageState status="error" title="수신그룹 목록을 불러오는데 실패했습니다." onRetry={fetchQueues} /></T.Td></T.Tr>
            : queues.length > 0 ? queues.map((queue, index) => (
              <T.Tr
                key={queue.id}
                $clickable
                onDoubleClick={(event) => {
                  if (isTableRowActionTarget(event.target)) return;
                  setSelectedQueue(queue);
                  setIsEditModalOpen(true);
                }}
              >
                <T.Td>{totalElements - (currentPage - 1) * 20 - index}</T.Td>
                <T.Td>{queue.displayName}</T.Td>
                <T.Td>{getStrategyLabel(queue.strategy)}</T.Td>
                <T.Td>{queue.memberCount}개</T.Td>
                <T.Td>{queue.strategy === 'SEQUENTIAL' ? formatTimeout(queue.ringTimeoutSeconds) : '-'}</T.Td>
                <T.Td>{formatTimeout(queue.totalTimeoutSeconds)}</T.Td>
                <T.Td>
                  <T.ActionButton style={{ marginRight: '4px' }} onClick={() => { setSelectedQueue(queue); setIsMemberEditModalOpen(true); }}>멤버 관리</T.ActionButton>
                  <T.ActionButton style={{ marginRight: '4px' }} onClick={() => { setSelectedQueue(queue); setIsEditModalOpen(true); }}>수정</T.ActionButton>
                  <T.ActionButton $variant="danger" onClick={() => { setSelectedQueue(queue); setIsConfirmOpen(true); }}>삭제</T.ActionButton>
                </T.Td>
              </T.Tr>
            )) : (
              <T.Tr>
                <T.Td colSpan={7}>
                  <PageState status="empty" title={appliedSearchTerm ? '검색 조건에 맞는 수신그룹이 없습니다.' : '등록된 수신그룹이 없습니다.'} />
                </T.Td>
              </T.Tr>
            )}
          </tbody>
        </T.Table>
      </T.TableArea>

      {!loading && !hasLoadError && <T.TableFooter>
        <T.TotalCount aria-label="조회 결과">총 <span>{totalElements.toLocaleString()}</span>건</T.TotalCount>
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      </T.TableFooter>}

      {isCreateModalOpen && (
        <QueueCreateModal onClose={() => setIsCreateModalOpen(false)} onSuccess={() => { setIsCreateModalOpen(false); fetchQueues(); }} />
      )}
      {isEditModalOpen && (
        <QueueEditModal queue={selectedQueue}
          onClose={() => { setIsEditModalOpen(false); setSelectedQueue(null); }}
          onSuccess={() => { setIsEditModalOpen(false); setSelectedQueue(null); fetchQueues(); }}
        />
      )}
      {isMemberEditModalOpen && (
        <QueueMemberEditModal queue={selectedQueue}
          onClose={() => { setIsMemberEditModalOpen(false); setSelectedQueue(null); }}
          onSuccess={() => { setIsMemberEditModalOpen(false); setSelectedQueue(null); fetchQueues(); }}
        />
      )}
      {isConfirmOpen && (
        <ConfirmModal
          title="수신그룹 삭제"
          message={`'${selectedQueue?.displayName || selectedQueue?.name}' 수신그룹을 삭제하시겠습니까?\n삭제된 데이터는 복구할 수 없습니다.`}
          confirmLabel="삭제하기" cancelLabel="취소"
          onConfirm={handleConfirmDelete}
          onCancel={() => setIsConfirmOpen(false)}
        />
      )}
    </T.PageContainer>
  );
};

export default QueueList;
